import type { AppState } from './model.ts';
import { SCHEMA_VERSION } from './model.ts';
import { canonicalJson, sha256Text } from './hash.ts';
import { createInitialState } from './defaults.ts';
import { Temporal } from './time.ts';
import { sanitizeUrl } from './url.ts';

/**
 * Fullständig säkerhetskopia av appdata (till skillnad från ICS-export). Innehåller egna aktiviteter,
 * importerade källposter med historik, lokala anteckningar/klar-status, importhistorik, mallar och
 * inställningar. Notisleveranslogg och enhetsdata ingår inte (de är per enhet).
 */
export interface BackupFile {
  format: 'autoday-backup';
  formatVersion: 1;
  schemaVersion: number;
  createdAt: string;
  app: string;
  sha256: string;
  state: AppState;
}

export function createBackup(state: AppState, now: Temporal.Instant): BackupFile {
  const body = canonicalJson(state);
  return {
    format: 'autoday-backup',
    formatVersion: 1,
    schemaVersion: state.schemaVersion,
    createdAt: now.toString({ smallestUnit: 'second' }),
    app: 'AutoDay 0.1',
    sha256: sha256Text(body),
    state,
  };
}

export class BackupError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BackupError';
  }
}

/** Versionerade migreringar: index = från-version. */
export const MIGRATIONS: Record<number, (s: AppState) => AppState> = {
  // 1 → 2 läggs till här när schemat ändras.
};

export function migrate(state: AppState): AppState {
  let s = state;
  let guard = 0;
  while (s.schemaVersion < SCHEMA_VERSION && guard++ < 100) {
    const m = MIGRATIONS[s.schemaVersion];
    if (!m) throw new BackupError(`Ingen migrering från schemaversion ${s.schemaVersion}.`);
    s = m(s);
  }
  if (s.schemaVersion > SCHEMA_VERSION) throw new BackupError(`Datan kommer från en nyare AutoDay (schema ${s.schemaVersion}). Uppdatera appen först.`);
  // Fyll i fält som saknas i äldre data utan att skriva över befintliga.
  const base = createInitialState();
  return {
    ...base,
    ...s,
    settings: { ...base.settings, ...s.settings, quietHours: { ...base.settings.quietHours, ...s.settings?.quietHours } },
    calendars: { ...base.calendars, ...s.calendars },
    categories: { ...base.categories, ...s.categories },
  };
}

export function parseBackup(json: string): AppState {
  if (json.length > 100 * 1024 * 1024) throw new BackupError('Säkerhetskopian är för stor.');
  let data: BackupFile;
  try {
    data = JSON.parse(json) as BackupFile;
  } catch {
    throw new BackupError('Filen är inte giltig JSON.');
  }
  if (data?.format !== 'autoday-backup') throw new BackupError('Filen är ingen AutoDay-säkerhetskopia.');
  if (data.formatVersion !== 1) throw new BackupError('Okänt säkerhetskopieformat.');
  if (!data.state || typeof data.state !== 'object') throw new BackupError('Säkerhetskopian saknar data.');
  if (sha256Text(canonicalJson(data.state)) !== data.sha256) throw new BackupError('Kontrollsumman stämmer inte – filen är skadad eller ändrad.');
  return withSafeLinks(migrate(data.state));
}

/**
 * Kontrollsumman skyddar bara mot skadade filer – vem som helst kan räkna om den. En säkerhetskopia är
 * därför opålitlig indata: länkar som inte är http(s) tas bort (samma regel som vid import och i
 * formuläret), så att en hopsnickrad fil aldrig kan lägga en javascript:-länk bakom "Öppna länk".
 */
function withSafeLinks(s: AppState): AppState {
  const events = Object.fromEntries(
    Object.entries(s.events).map(([k, e]) => {
      if (e.url === undefined || sanitizeUrl(e.url).url) return [k, e];
      const { url: _drop, ...rest } = e;
      void _drop;
      return [k, rest];
    }),
  ) as AppState['events'];
  const sourceItems = Object.fromEntries(
    Object.entries(s.sourceItems).map(([k, si]) => {
      const u = si.data?.url;
      if (u === undefined || u === null || sanitizeUrl(u).url) return [k, si];
      return [k, { ...si, data: { ...si.data, url: null } }];
    }),
  ) as AppState['sourceItems'];
  return { ...s, events, sourceItems };
}

/** Provåterställning: tolkar och validerar utan att ersätta befintlig data. */
export function verifyBackup(json: string): { ok: true; summary: { events: number; sourceItems: number; imports: number; templates: number } } | { ok: false; error: string } {
  try {
    const s = parseBackup(json);
    return {
      ok: true,
      summary: {
        events: Object.values(s.events).filter((e) => !e.deleted).length,
        sourceItems: Object.keys(s.sourceItems).length,
        imports: Object.values(s.sources).reduce((n, x) => n + x.imports.length, 0),
        templates: Object.keys(s.templates).length,
      },
    };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
